import { useState } from "react";
import { MessageCircle, AlertTriangle, Send, Clock } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState } from "@/components/admin/shared";
import {
  useWhatsAppHistory,
  useWhatsAppTemplates,
  useWhatsAppBroadcast,
} from "@/hooks/admin/useCRM";
import type { SegmentKey, WhatsAppBroadcastPayload } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type Tab = "new" | "history";

const SEGMENT_OPTIONS: Array<{ key: SegmentKey; label: string }> = [
  { key: "vip", label: "VIP" },
  { key: "at_risk", label: "À risque" },
  { key: "dormant", label: "Dormant" },
  { key: "new", label: "Nouveau" },
  { key: "high_frequency", label: "Haute fréquence" },
  { key: "cod_only", label: "COD uniquement" },
  { key: "mobile_money_only", label: "Mobile money uniquement" },
];

// ---------------------------------------------------------------------------
// Tab nav
// ---------------------------------------------------------------------------

function TabNav({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {([
        { key: "new" as Tab, label: "Nouvelle campagne" },
        { key: "history" as Tab, label: "Historique" },
      ] as const).map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New campaign form
// ---------------------------------------------------------------------------

function NewCampaignTab() {
  const { data: templatesData, isLoading: loadingTemplates } = useWhatsAppTemplates();
  const broadcast = useWhatsAppBroadcast();

  const [segmentKey, setSegmentKey] = useState<SegmentKey | "">("");
  const [templateId, setTemplateId] = useState("");
  const [messageOverride, setMessageOverride] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const templates = templatesData?.templates ?? [];

  const handleSubmit = () => {
    if (!segmentKey) { toast.error("Veuillez sélectionner un segment."); return; }
    if (!templateId && !messageOverride.trim()) {
      toast.error("Veuillez sélectionner un modèle ou saisir un message.");
      return;
    }

    const payload: WhatsAppBroadcastPayload = {
      segment_key: segmentKey as SegmentKey,
      template_id: templateId,
      ...(messageOverride.trim() && { message_override: messageOverride }),
      ...(imageUrl.trim() && { image_url: imageUrl }),
      scheduled_at: scheduledAt || null,
    };

    broadcast.mutate(payload, {
      onSuccess: () => {
        toast.success(
          scheduledAt ? "Campagne programmée avec succès." : "Campagne envoyée avec succès.",
        );
        setSegmentKey("");
        setTemplateId("");
        setMessageOverride("");
        setImageUrl("");
        setScheduledAt("");
      },
      onError: () => toast.error("Erreur lors de l'envoi de la campagne."),
    });
  };

  return (
    <div className="max-w-xl space-y-5">
      {/* Compliance notice */}
      <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
        <AlertTriangle className="h-5 w-5 shrink-0 text-orange-500 mt-0.5" />
        <div className="text-sm text-orange-800">
          <p className="font-semibold">Conformité opt-in obligatoire</p>
          <p className="mt-0.5 text-orange-700">
            Les messages WhatsApp ne sont envoyés qu'aux clients ayant expressément consenti à recevoir des communications.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-5">
        {/* Segment */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Segment cible <span className="text-red-500">*</span>
          </label>
          <select
            value={segmentKey}
            onChange={(e) => setSegmentKey(e.target.value as SegmentKey | "")}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Sélectionner un segment...</option>
            {SEGMENT_OPTIONS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Template */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">Modèle de message</label>
          {loadingTemplates ? (
            <div className="h-10 rounded-lg bg-stone-100 animate-pulse" />
          ) : (
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">Aucun modèle (saisir manuellement)</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Message override */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Message personnalisé
            {!templateId && <span className="text-red-500"> *</span>}
          </label>
          <textarea
            value={messageOverride}
            onChange={(e) => setMessageOverride(e.target.value)}
            rows={4}
            placeholder="Bonjour {prénom}, découvrez notre nouvelle promotion..."
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">Champs disponibles : {"{prénom}"}, {"{ref_commande}"}</p>
        </div>

        {/* Image URL */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">URL de l'image (optionnel)</label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Schedule */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            Programmer l'envoi (laisser vide pour envoyer maintenant)
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={broadcast.isPending}
          className="w-full gap-2"
        >
          <Send className="h-4 w-4" />
          {broadcast.isPending
            ? "Envoi en cours..."
            : scheduledAt
              ? "Programmer la campagne"
              : "Envoyer maintenant"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTab() {
  const { data, isLoading } = useWhatsAppHistory();
  const broadcasts = data?.broadcasts ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[...Array(5)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
      </div>
    );
  }

  if (broadcasts.length === 0) {
    return (
      <EmptyState
        icon={<MessageCircle className="h-6 w-6" />}
        title="Aucune campagne envoyée"
        description="Créez votre première campagne WhatsApp depuis l'onglet Nouvelle campagne."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 bg-stone-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Segment</th>
              <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Aperçu du message</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Envoyé</th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Livré</th>
              <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Lu</th>
              <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {broadcasts.map((b) => (
              <tr key={b.id} className="hover:bg-stone-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700">
                    {b.segment_name}
                  </span>
                </td>
                <td className="hidden md:table-cell px-4 py-3 max-w-xs truncate text-stone-600">
                  {b.message_preview}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-800">
                  {b.sent_count.toLocaleString("fr-SN")}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                  {b.delivered_count.toLocaleString("fr-SN")}
                </td>
                <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                  {b.read_count.toLocaleString("fr-SN")}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-stone-500">
                  {b.scheduled_at
                    ? `Programmé — ${new Date(b.scheduled_at).toLocaleDateString("fr-SN")}`
                    : new Date(b.created_at).toLocaleDateString("fr-SN")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function WhatsApp() {
  const [tab, setTab] = useState<Tab>("new");

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Campagnes WhatsApp
            </h1>
            <p className="text-sm text-stone-500">
              Diffusions ciblées par segment client
            </p>
          </div>
        </div>

        <TabNav active={tab} onChange={setTab} />

        {tab === "new" && <NewCampaignTab />}
        {tab === "history" && <HistoryTab />}
      </div>
    </AdminLayout>
  );
}
